import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, fecha, hoy, primeroDelMes } from '../utils/formato';

// Gastos del negocio.
//
// Todo lo que sale y no es mercancía ni mano de obra: el arriendo, la luz, el
// transporte. Mientras esto no se registre, la ganancia del mes se ve más
// grande de lo que es, porque solo se le está restando lo que costó la
// mercancía.
//
// Las categorías van fijas a propósito. Si se escriben a mano, en tres meses
// hay «arriendo», «Arriendo» y «arriendo local» y ya no se puede sumar.

const CATEGORIAS = [
  { id: 'Arriendo',   ico: '🏠' },
  { id: 'Servicios',  ico: '💡' },
  { id: 'Transporte', ico: '🚚' },
  { id: 'Herramienta',ico: '🔧' },
  { id: 'Impuestos',  ico: '🧾' },
  { id: 'Otros',      ico: '📌' },
];

const VACIO = () => ({
  fecha: hoy(), categoria: 'Arriendo', monto: '',
  forma_pago: 'efectivo', descripcion: '',
});

function Gastos({ onCambio }) {
  const [gastos, setGastos] = useState([]);
  const [desde, setDesde] = useState(primeroDelMes());
  const [hasta, setHasta] = useState(hoy());
  const [nuevo, setNuevo] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);

  const cargar = useCallback(() => {
    axios.get('/api/gastos').then(r => setGastos(r.data || [])).catch(() => {});
  }, []);

  useEffect(cargar, [cargar]);

  const enRango = gastos.filter(g => g.fecha >= desde && g.fecha <= hasta);
  const total = enRango.reduce((s, g) => s + (g.monto || 0), 0);

  const porCategoria = CATEGORIAS.map(c => ({
    ...c,
    total: enRango.filter(g => g.categoria === c.id).reduce((s, g) => s + g.monto, 0),
  })).filter(c => c.total > 0).sort((a, b) => b.total - a.total);

  const guardar = async (e) => {
    e.preventDefault();
    const monto = parseFloat(nuevo.monto);
    if (!(monto > 0)) return setAviso({ tipo: 'malo', texto: 'El gasto tiene que ser mayor que cero' });
    setGuardando(true);
    try {
      await axios.post('/api/gastos', { ...nuevo, monto });
      setAviso({ tipo: 'bueno', texto: `${nuevo.categoria} de ${plata(monto)} registrado` });
      setNuevo(null);
      cargar();
      onCambio && onCambio();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo guardar' });
    }
    setGuardando(false);
  };

  const borrar = async (g) => {
    if (!window.confirm(`¿Borrar el gasto de ${plata(g.monto)} en ${g.categoria}?`)) return;
    try {
      await axios.delete(`/api/gastos/${g.id}`);
      cargar();
      onCambio && onCambio();
    } catch {
      setAviso({ tipo: 'malo', texto: 'No se pudo borrar' });
    }
  };

  return (
    <>
      <div className="encabezado">
        <h1>Gastos</h1>
        <p>Lo que sale y no es mercancía: arriendo, servicios, transporte</p>
      </div>

      {aviso && <div className={'aviso ' + aviso.tipo}>{aviso.texto}</div>}

      <div className="bloque">
        <div className="cuerpo">
          <div className="campos">
            <label className="campo">
              <span>Desde</span>
              <input type="date" value={desde} onChange={e => setDesde(e.target.value)} />
            </label>
            <label className="campo">
              <span>Hasta</span>
              <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} />
            </label>
          </div>
        </div>
      </div>

      <div className="rejilla">
        <div className="dato" style={{ '--color': 'var(--malo)' }}>
          <div className="rotulo">💸 Gastado</div>
          <div className="valor cifra">{plata(total)}</div>
          <div className="pie">
            {enRango.length === 1 ? '1 gasto' : `${enRango.length} gastos`} en el periodo
          </div>
        </div>
        {porCategoria.slice(0, 2).map(c => (
          <div key={c.id} className="dato" style={{ '--color': 'var(--acero)' }}>
            <div className="rotulo">{c.ico} {c.id}</div>
            <div className="valor cifra">{plata(c.total)}</div>
            <div className="pie">
              {total > 0 ? Math.round(c.total / total * 100) : 0}% de lo gastado
            </div>
          </div>
        ))}
      </div>

      <div className="bloque">
        <h2>💸 Anotar un gasto</h2>
        <div className="cuerpo">
          {!nuevo ? (
            <div className="acciones">
              <button className="boton grande" onClick={() => setNuevo(VACIO())}>
                ➕ Nuevo gasto
              </button>
            </div>
          ) : (
            <form onSubmit={guardar}>
              <div className="campos">
                <label className="campo">
                  <span>Cuánto *</span>
                  <input type="number" step="1" min="0" className="num-grande" required autoFocus
                         value={nuevo.monto} placeholder="0"
                         onChange={e => setNuevo({ ...nuevo, monto: e.target.value })} />
                </label>
                <label className="campo">
                  <span>De qué</span>
                  <select value={nuevo.categoria}
                          onChange={e => setNuevo({ ...nuevo, categoria: e.target.value })}>
                    {CATEGORIAS.map(c => (
                      <option key={c.id} value={c.id}>{c.ico} {c.id}</option>
                    ))}
                  </select>
                </label>
                <label className="campo">
                  <span>Fecha</span>
                  <input type="date" value={nuevo.fecha}
                         onChange={e => setNuevo({ ...nuevo, fecha: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Cómo pagó</span>
                  <select value={nuevo.forma_pago}
                          onChange={e => setNuevo({ ...nuevo, forma_pago: e.target.value })}>
                    <option value="efectivo">💵 Efectivo</option>
                    <option value="transferencia">🏦 Transferencia</option>
                  </select>
                </label>
              </div>
              <label className="campo" style={{ marginTop: 12 }}>
                <span>Detalle</span>
                <input value={nuevo.descripcion} placeholder="Arriendo de agosto, recibo de luz…"
                       onChange={e => setNuevo({ ...nuevo, descripcion: e.target.value })} />
              </label>
              <div className="acciones">
                <button type="button" className="boton suave" onClick={() => setNuevo(null)}>
                  Cancelar
                </button>
                <button className="boton" disabled={guardando}>
                  {guardando ? 'Guardando…' : '💾 Guardar gasto'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <div className="bloque">
        <h2>📋 Gastos del periodo <span className="contador">{enRango.length}</span></h2>
        {enRango.length === 0 ? (
          <div className="vacio">
            No hay gastos anotados en estas fechas. Mientras no los anotes, la ganancia
            que ves en Ganancias está sin descontarlos.
          </div>
        ) : (
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>De qué</th>
                  <th>Detalle</th>
                  <th>Pago</th>
                  <th style={{ textAlign: 'right' }}>Monto</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {enRango.map(g => {
                  const cat = CATEGORIAS.find(c => c.id === g.categoria);
                  return (
                    <tr key={g.id}>
                      <td>{fecha(g.fecha)}</td>
                      <td className="recio">{cat ? cat.ico : '📌'} {g.categoria}</td>
                      <td style={{ fontSize: 13 }}>{g.descripcion || '—'}</td>
                      <td>
                        <span className={'pinta ' + (g.forma_pago === 'efectivo' ? 'bueno' : 'acero')}>
                          {g.forma_pago}
                        </span>
                      </td>
                      <td className="num recio">{plata(g.monto)}</td>
                      <td>
                        <button className="boton chico suave" onClick={() => borrar(g)}>✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

export default Gastos;
